import { AddCircle, AddShoppingCartOutlined, MoreVert, ExpandMore, WhatsApp, LocalOffer as LocalOfferIcon, TrendingDown as TrendingDownIcon, Add as AddIcon } from '@mui/icons-material';
import { Box, Card, CardContent, CardMedia, IconButton, ListItem, Typography, CardActions, Collapse, styled, Button, Chip, Stack, Switch, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip } from '@mui/material';
import React from "react";
import { alpha, useTheme } from '@mui/material/styles';
import { Banner, MenuCustom, VariantsDisplay } from '../../../components';
import { OPERATION } from "../../../helpers/enums";
import { ccyFormat } from "../../../helpers/helper";
import ArticuloService from '../../../services/articulos';
import ImagenesAIService from '../../../services/imagenesAI';
import ImagenesService from '../../../services/imagenes';
import VariantCard from '../../FormArticulo/VariantCard';
import './styles.css';

const ExpandMoreStyled = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme }) => ({
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
    variants: [
        {
            props: ({ expand }) => !expand,
            style: {
                transform: 'rotate(0deg)',
            },
        },
        {
            props: ({ expand }) => !!expand,
            style: {
                transform: 'rotate(180deg)',
            },
        },
    ],
}));

const ArticleData = ({ data, optionSelected, addToCart, options, readonly = false, imagesOnly = false, colorBackground = 'background.default', colorFont = 'text.primary', onSendMessage, onWebUpdated, isAdmin = false }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = React.useState(false);
    const [webEnabled, setWebEnabled] = React.useState(!!data.web);
    const [webModalOpen, setWebModalOpen] = React.useState(false);
    const [draftWebEnabled, setDraftWebEnabled] = React.useState(!!data.web);
    const [updatingWeb, setUpdatingWeb] = React.useState(false);
    const [editData, setEditData] = React.useState({
        name: data?.name || '',
        description: data?.description || '',
        sale_price: Number(data?.salePrice ?? data?.price ?? 0),
        images: data?.images || []
    });
    const [newImageFiles, setNewImageFiles] = React.useState([]);
    const [imagePrompt, setImagePrompt] = React.useState('');
    const [typeDetails, setTypeDetails] = React.useState([]);
    const [variantDialog, setVariantDialog] = React.useState({ open: false, pendingCartData: null });
    const [selectedColor, setSelectedColor] = React.useState(null);
    const [selectedSize, setSelectedSize] = React.useState(null);
    const webImagesInputRef = React.useRef(null);

    React.useEffect(() => {
        setWebEnabled(!!data.web);
    }, [data.web]);

    React.useEffect(() => {
        setEditData({
            name: data?.name || '',
            description: data?.description || '',
            sale_price: Number(data?.salePrice ?? data?.price ?? 0),
            images: data?.images || []
        });
    }, [data?.name, data?.description, data?.salePrice, data?.price, data?.images]);

    React.useEffect(() => {
        const variantsWithIds = (data?.type_detail || []).map((variant, idx) => ({
            ...variant,
            _id: variant._id || `variant-${Date.now()}-${idx}-${Math.random()}`
        }));
        setTypeDetails(variantsWithIds);
    }, [data?.type_detail]);
    
    // Calcular la mejor oferta aplicable
    const getBestOffer = () => {
        if (!data.applicable_offers || data.applicable_offers.length === 0) return null;
        
        // Ordenar por prioridad (mayor primero) y tomar la primera
        const sortedOffers = [...data.applicable_offers].sort((a, b) => b.priority - a.priority);
        const bestOffer = sortedOffers[0];
        
        // Calcular precio final
        let finalPrice = data.price;
        let discountAmount = 0;
        
        if (bestOffer.type === 'percentage' && bestOffer.discount_percentage) {
            discountAmount = (data.price * bestOffer.discount_percentage) / 100;
            finalPrice = data.price - discountAmount;
        } else if (bestOffer.type === 'fixed_amount' && bestOffer.discount_amount) {
            discountAmount = bestOffer.discount_amount;
            finalPrice = data.price - discountAmount;
        }
        
        return {
            ...bestOffer,
            final_price: Math.max(0, finalPrice),
            discount_amount: discountAmount
        };
    };

    const bestOffer = getBestOffer();

    const handleAddToCart = () => {
        if (addToCart) {
            const cartData = bestOffer ? {
                ...data,
                original_price: data.price,
                discount_price: bestOffer.final_price,
                applied_offer: {
                    id: bestOffer._id,
                    name: bestOffer.name,
                    type: bestOffer.type,
                    discount_percentage: bestOffer.discount_percentage,
                    discount_amount: bestOffer.discount_amount,
                    final_price: bestOffer.final_price
                }
            } : data;

            // Si el artículo tiene variantes con stock, mostrar selector primero
            const availableVariants = (data.type_detail || []).filter(v => v.available && v.quantity > 0);
            if (availableVariants.length > 0) {
                setSelectedColor(null);
                setSelectedSize(null);
                setVariantDialog({ open: true, pendingCartData: cartData });
                return;
            }

            addToCart(cartData);
        }
    };

    const handleConfirmVariant = () => {
        if (!variantDialog.pendingCartData || !selectedColor) return;
        addToCart({
            ...variantDialog.pendingCartData,
            color_selected: selectedColor,
            size_selected: selectedSize || '',
        });
        setVariantDialog({ open: false, pendingCartData: null });
        setSelectedColor(null);
        setSelectedSize(null);
    };

    const handleCloseVariantDialog = () => {
        setVariantDialog({ open: false, pendingCartData: null });
        setSelectedColor(null);
        setSelectedSize(null);
    };

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const canUpdateWeb = !!(data?.storeId && (data?.articleId || data?.id));

    const buildBasePayload = () => ({
        article: {
            name: (editData?.name || data?.name || '').trim(),
            description: (editData?.description || data?.description || '').trim(),
            images: Array.isArray(editData?.images) ? editData.images : (Array.isArray(data?.images) ? data.images : []),
            web: !!webEnabled
        },
        prices: {
            list_price: Number(data?.listPrice ?? data?.price ?? 0),
            sale_price: Number(editData?.sale_price ?? data?.salePrice ?? data?.price ?? 0)
        },
        customer: data?.customerId || '',
        type_detail: typeDetails.map(({ _id, ...rest }) => rest)
    });

    const handlePersistWeb = async (nextValue, payloadOverrides = {}) => {
        if (!canUpdateWeb) return;
        setUpdatingWeb(true);
        try {
            const articleId = data.id || data.articleId;
            const basePayload = buildBasePayload();
            const finalPayload = {
                ...basePayload,
                ...payloadOverrides,
                article: {
                    ...basePayload.article,
                    ...(payloadOverrides?.article || {}),
                    web: payloadOverrides?.article?.web ?? nextValue
                },
                prices: {
                    ...basePayload.prices,
                    ...(payloadOverrides?.prices || {})
                },
                customer: payloadOverrides?.customer ?? basePayload.customer
            };

            const typeDetailToSave = Array.isArray(payloadOverrides?.type_detail)
                ? payloadOverrides.type_detail
                : basePayload.type_detail;

            await ArticuloService.update(data.storeId, articleId, finalPayload);

            // if (Array.isArray(typeDetailToSave)) {
            //     await ArticuloService.updateVariants(data.storeId, articleId, typeDetailToSave);
            // }

            setWebEnabled(nextValue);
            onWebUpdated && onWebUpdated(articleId, {
                web: nextValue,
                ...(payloadOverrides?.article ? {
                    name: payloadOverrides.article.name,
                    description: payloadOverrides.article.description,
                    images: payloadOverrides.article.images
                } : {}),
                ...(payloadOverrides?.prices ? {
                    sale_price: payloadOverrides.prices.sale_price
                } : {}),
                ...(typeDetailToSave ? {
                    type_detail: typeDetailToSave
                } : {})
            });
        } catch (err) {
            console.error('Error actualizando disponibilidad web', err);
            setWebEnabled((prev) => !nextValue ? prev : false);
        } finally {
            setUpdatingWeb(false);
        }
    };

    const handleSelectImages = (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;
        setNewImageFiles((prev) => [...prev, ...files]);
    };

    const handleRemoveExistingImage = (indexToRemove) => {
        setEditData((prev) => ({
            ...prev,
            images: prev.images.filter((_, index) => index !== indexToRemove)
        }));
    };

    const handleRemoveNewImage = (indexToRemove) => {
        setNewImageFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleAddVariant = () => {
        setTypeDetails((prev) => ([
            ...prev,
            {
                color: '',
                sizes: [],
                images: [],
                available: true,
                quantity: 0,
                _id: `variant-${Date.now()}-${prev.length}-${Math.random()}`
            }
        ]));
    };

    const handleRemoveVariant = (index) => {
        setTypeDetails((prev) => prev.filter((_, i) => i !== index));
    };

    const handleVariantChange = (index, field, value) => {
        setTypeDetails((prev) => prev.map((variant, i) => i === index ? { ...variant, [field]: value } : variant));
    };

    const handleAddSize = (index, size) => {
        const trimmed = (size || '').trim();
        if (!trimmed) return;
        setTypeDetails((prev) => prev.map((variant, i) => {
            if (i !== index) return variant;
            const sizes = Array.isArray(variant.sizes) ? variant.sizes : [];
            if (sizes.includes(trimmed)) return variant;
            return { ...variant, sizes: [...sizes, trimmed] };
        }));
    };

    const handleRemoveSize = (variantIndex, size) => {
        setTypeDetails((prev) => prev.map((variant, i) => {
            if (i !== variantIndex) return variant;
            return { ...variant, sizes: (variant.sizes || []).filter((s) => s !== size) };
        }));
    };

    const handleSizeDetailsChange = (index, newSizeDetails, totalQty) => {
        setTypeDetails((prev) => prev.map((variant, i) =>
            i === index
                ? { ...variant, size_details: newSizeDetails, quantity: totalQty }
                : variant
        ));
    };

    const handleAddVariantImage = (variantIndex, imageUrl) => {
        const trimmed = (imageUrl || '').trim();
        if (!trimmed) return;
        setTypeDetails((prev) => prev.map((variant, i) => {
            if (i !== variantIndex) return variant;
            const images = Array.isArray(variant.images) ? variant.images : [];
            if (images.includes(trimmed)) return variant;
            return { ...variant, images: [...images, trimmed] };
        }));
    };

    const handleRemoveVariantImage = (variantIndex, imageUrl) => {
        setTypeDetails((prev) => prev.map((variant, i) => {
            if (i !== variantIndex) return variant;
            return { ...variant, images: (variant.images || []).filter((img) => img !== imageUrl) };
        }));
    };

    const handleChangeWebSwitch = async (event) => {
        const nextValue = event.target.checked;
        if (nextValue) {
            setDraftWebEnabled(true);
            setWebModalOpen(true);
            return;
        }

        await handlePersistWeb(false);
    };

    const handleConfirmEnableWeb = async () => {
        if (!isValidForm) {
            return;
        }

        let uploaded = [];
        if (newImageFiles.length > 0) {
            const formData = new FormData();
            newImageFiles.forEach((file) => formData.append('image', file));
            if (imagePrompt.trim()) {
                formData.append('prompt', imagePrompt.trim());
                uploaded = await ImagenesAIService.generate(formData);
            } else {
                uploaded = await ImagenesService.upload(formData);
            }
        }

        const finalImages = [...(editData.images || []), ...uploaded];

        await handlePersistWeb(draftWebEnabled, {
            article: {
                name: editData.name,
                description: editData.description,
                images: finalImages,
                web: draftWebEnabled
            },
            prices: {
                list_price: Number(data?.listPrice ?? data?.price ?? 0),
                sale_price: Number(editData.sale_price)
            },
            customer: data?.customerId || '',
            type_detail: typeDetails.map(({ _id, ...rest }) => rest)
        });

        setNewImageFiles([]);
        setWebModalOpen(false);
    };

    const normalizedName = (editData?.name || '').trim();
    const normalizedDescription = (editData?.description || '').trim();
    const normalizedSalePrice = Number(editData?.sale_price || 0);
    const totalImagesCount = (editData?.images?.length || 0) + (newImageFiles?.length || 0);

    const nameError = normalizedName.length === 0;
    const descriptionError = normalizedDescription.length === 0;
    const salePriceError = normalizedSalePrice <= 0;
    const imagesError = totalImagesCount < 1;
    const isValidForm = !nameError && !descriptionError && !salePriceError && !imagesError;

    return <ListItem
        sx={{ display: 'flex', justifyContent: 'space-between', background: colorBackground, color: colorFont, padding: imagesOnly ? 0 : 1 }}
        alignItems="flex-start"
    >
        <Card elevation={4} sx={{ 
            display: 'flex', 
            flexDirection: imagesOnly ? 'column' : 'row', 
            width: '100%', 
            background: imagesOnly
                ? 'background.paper'
                : data.to === OPERATION.VENDER
                    ? alpha(theme.palette.success.main, 0.12)
                    : alpha(theme.palette.error.main, 0.12),
            position: 'relative',
            overflow: 'visible',
            border: data.newArrival ? `3px solid ${theme.palette.success.main}` : 'none',
            boxShadow: data.newArrival ? `0 4px 20px ${alpha(theme.palette.success.main, 0.3)}` : undefined
        }}>
            {/* Badge flotante de NUEVO */}
            {data.newArrival && !imagesOnly && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -8,
                        left: -8,
                        background: theme.palette.success.main,
                        color: 'common.white',
                        borderRadius: '8px',
                        px: 1.5,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
                        zIndex: 10,
                        transform: 'rotate(-5deg)'
                    }}
                >
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1, fontWeight: 'bold', letterSpacing: 1 }}>
                        ✨ NUEVO
                    </Typography>
                </Box>
            )}
            {/* Badge flotante de descuento */}
            {bestOffer && !imagesOnly && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        background: theme.palette.error.main,
                        color: 'common.white',
                        borderRadius: '50%',
                        width: 50,
                        height: 50,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
                        zIndex: 10
                    }}
                >
                    <Typography variant="caption" sx={{ fontSize: '1rem', lineHeight: 1, fontWeight: 'bold' }}>
                        -{bestOffer.discount_percentage || Math.round((bestOffer.discount_amount / data.price) * 100)}%
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1 }}>
                        OFF
                    </Typography>
                </Box>
            )}
            {imagesOnly && <Box display='flex' flexDirection='row' justifyContent='space-between' padding={1}>
                <Box>
                    <Typography variant="caption" sx={{
                        flexGrow: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {data.name}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary" component="div" sx={{fontStyle: 'italic'}}>
                        {data.description}
                    </Typography> */}
                </Box>
                {/* {imagesOnly && <Typography variant="caption" display='flex' alignItems='center' flexDirection='row' aria-label="delete" alignContent='center'>
                    {`$${ccyFormat(data.price)}`}
                </Typography>} */}
            </Box>}
            <CardMedia
                sx={{ 
                    objectFit: 'contain', 
                    width: imagesOnly ? '100%' : '50%', 
                    alignContent: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    maxHeight: imagesOnly ? 'auto' : '250px'
                }}
                component='div'
            >
                <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
                    <Banner 
                        images={data.images} 
                        onlyImages={imagesOnly} 
                        height={imagesOnly ? 200 : 250}
                    />
                </Box>
                {data.quantity === 0 && <div className="out-of-stock">SIN STOCK</div>}
            </CardMedia>
            {!imagesOnly ? <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', justifyContent: 'space-between' }}>
                <Box display='flex' flexDirection='row'>
                    <CardContent sx={{ flex: '1 0 auto', display: 'flex', justifyContent: 'flex-start', flexDirection: 'column', paddingRight: 0, width: '80%', }}>
                        <Typography component="span" variant="caption" sx={{
                            flexGrow: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {data.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                            {data.description}
                        </Typography>
                    </CardContent>
                    {options && !readonly && <Box sx={{ marginTop: bestOffer ? '30px' : '0px' }}>
                        <MenuCustom
                            Child={() => <MoreVert />}
                            options={options}
                            optionsSelected={(idoption) => optionSelected(idoption, data)}
                        />
                    </Box>}
                </Box>
                <Box display='flex' flexDirection='row' justifyContent='space-between' paddingBottom={1}>
                    <Box display='flex' flexDirection='column' justifyContent='flex-start' paddingLeft='15px'>
                        {/* Precio con oferta aplicada */}
                        {bestOffer ? (
                            <Box>
                                <Box display='flex' alignItems='center' gap={0.5}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            textDecoration: 'line-through',
                                            color: 'text.secondary',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        ${ccyFormat(data.price)}
                                    </Typography>
                                    <Chip
                                        label={`-${bestOffer.discount_percentage || Math.round((bestOffer.discount_amount / data.price) * 100)}%`}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.65rem',
                                            fontWeight: 'bold',
                                            background: theme.palette.error.main,
                                            color: 'common.white'
                                        }}
                                    />
                                </Box>
                                <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 700 }}>
                                    ${ccyFormat(bestOffer.final_price)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'success.main',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.3,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    <TrendingDownIcon sx={{ fontSize: 12 }} />
                                    Ahorras ${ccyFormat(bestOffer.discount_amount)}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="h6" aria-label="delete">
                                ${ccyFormat(data.price)}
                            </Typography>
                        )}
                        
                        <Typography variant="caption" color={'text.secondary'} sx={{ fontStyle: 'italic', whiteSpace: 'pre-line', mt: 0.5 }}>
                            {`CODIGO: ${data.sku}`}
                        </Typography>
                        {OPERATION.VENDER === data.to &&
                            <Typography sx={{ fontWeight: 'bold' }} variant="caption" color="text.secondary" component="div">
                                {`Disponible: ${data.quantity}`}
                            </Typography>}
                        <Box display='flex' alignItems='center' gap={0.5} mt={0.4}>
                            <Typography variant="caption" color="text.secondary" component="span">
                                Web
                            </Typography>
                            <Switch
                                size='small'
                                checked={webEnabled}
                                disabled={!isAdmin || !canUpdateWeb || updatingWeb}
                                onChange={handleChangeWebSwitch}
                                inputProps={{ 'aria-label': 'Disponible en web' }}
                            />
                        </Box>
                        
                        {/* Stack de ofertas aplicables */}
                        {data.applicable_offers && data.applicable_offers.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 0.3 }}>
                                    Ofertas aplicables:
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                    {data.applicable_offers.map((offer) => (
                                        <Chip
                                            key={offer._id}
                                            label={offer.name}
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                                            sx={{ fontSize: '0.65rem', height: 22 }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Variantes (Colores y Tallas) - Componente reutilizable */}
                        <VariantsDisplay typeDetail={data.type_detail} />
                    </Box>
                    {!readonly && isAdmin && <Box display='flex' flexDirection='row' alignItems='flex-end'>
                        <IconButton
                            id="add-cart"
                            onClick={handleAddToCart}
                            disabled={(OPERATION.VENDER === data.to && data.quantity === 0)}
                        >
                            {OPERATION.VENDER === data.to ? <AddCircle /> : <AddShoppingCartOutlined />}
                        </IconButton>
                    </Box>}
                </Box>
            </Box> : <Box margin={0} padding={0}>
                {/* Dialog selector de variante para el cajero */}
                {variantDialog.open && (() => {
                    const availableVariants = (data.type_detail || []).filter(v => v.available && v.quantity > 0);
                    const selectedVariant = availableVariants.find(v => v.color === selectedColor);
                    const canConfirm = !!selectedColor && (!selectedVariant?.sizes?.length || !!selectedSize);
                    return (
                        <Dialog open onClose={handleCloseVariantDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                            <DialogTitle sx={{ pb: 1, background: 'linear-gradient(135deg,#673ab7 0%,#9c27b0 100%)', color: 'white', fontSize: '1rem', fontWeight: 700 }}>
                                {data.name}
                            </DialogTitle>
                            <DialogContent sx={{ pt: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block', mb: 1 }}>Color</Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.8} mb={2}>
                                    {availableVariants.map((v, i) => (
                                        <Tooltip key={i} title={`Stock: ${v.quantity}`} placement="top">
                                            <Chip
                                                label={`${v.color} (${v.quantity})`}
                                                onClick={() => { setSelectedColor(v.color); setSelectedSize(null); }}
                                                sx={{
                                                    textTransform: 'capitalize', fontWeight: 700,
                                                    background: selectedColor === v.color
                                                        ? 'linear-gradient(135deg,#673ab7 0%,#9c27b0 100%)'
                                                        : undefined,
                                                    color: selectedColor === v.color ? 'white' : undefined,
                                                }}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>

                                {selectedVariant?.sizes?.length > 0 && (
                                    <>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block', mb: 1 }}>Talla</Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.8}>
                                            {selectedVariant.sizes.map((size, i) => {
                                                const sd = selectedVariant.size_details?.find(s => s.size === size);
                                                const outOfStock = sd ? sd.quantity === 0 || sd.available === false : false;
                                                return (
                                                    <Tooltip key={i} title={outOfStock ? 'Sin stock' : sd ? `Stock: ${sd.quantity}` : ''} placement="top">
                                                        <span>
                                                            <Chip
                                                                label={size}
                                                                disabled={outOfStock}
                                                                onClick={outOfStock ? undefined : () => setSelectedSize(size)}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    background: selectedSize === size
                                                                        ? 'linear-gradient(135deg,#1976d2 0%,#42a5f5 100%)'
                                                                        : undefined,
                                                                    color: selectedSize === size ? 'white' : undefined,
                                                                }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </>
                                )}
                            </DialogContent>
                            <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                                <Button onClick={handleCloseVariantDialog} variant="outlined" size="small">Cancelar</Button>
                                <Button
                                    onClick={handleConfirmVariant}
                                    variant="contained"
                                    size="small"
                                    disabled={!canConfirm}
                                    sx={{ background: 'linear-gradient(135deg,#673ab7 0%,#9c27b0 100%)' }}
                                >
                                    Agregar al carrito
                                </Button>
                            </DialogActions>
                        </Dialog>
                    );
                })()}
                <CardActions disableSpacing sx={{ alignItems: "stretch", display: 'flex', width: '100%', flexDirection: 'column', margin: 0, padding: 0 }}>
                    {/* <IconButton aria-label="add to favorites">
                        <Favorite />
                    </IconButton> */}
                    <Box display='flex'>
                        <IconButton aria-label="share" onClick={() => onSendMessage(data)}>
                            <WhatsApp sx={{ color: 'success.main' }}/>
                        </IconButton>
                        <ExpandMoreStyled
                            expand={expanded}
                            onClick={handleExpandClick}
                            aria-expanded={expanded}
                            aria-label="show more"
                        >
                            <ExpandMore />
                        </ExpandMoreStyled>
                    </Box>
                    <Button size="medium" >
                        {`$${ccyFormat(data.price)}`}
                    </Button>
                </CardActions>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <CardContent>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }}>
                            {data.description}
                        </Typography>
                    </CardContent>
                </Collapse>
            </Box>}
        </Card>
        <Dialog
            open={webModalOpen}
            onClose={() => !updatingWeb && setWebModalOpen(false)}
            fullWidth
            maxWidth='xs'
            disableEnforceFocus
            disableRestoreFocus
        >
            <DialogTitle>Habilitar publicación web</DialogTitle>
            <DialogContent>
                <Box display='flex' flexDirection='column' gap={1.5} pt={0.5}>
                    <Typography variant='body2'>Vas a habilitar este artículo para la web y podés editar sus datos principales.</Typography>
                    <TextField
                        label='Nombre'
                        size='small'
                        value={editData.name}
                        onChange={(event) => setEditData((prev) => ({ ...prev, name: event.target.value }))}
                        disabled={updatingWeb}
                        error={nameError}
                        helperText={nameError ? 'El nombre es obligatorio' : ''}
                        fullWidth
                    />
                    <TextField
                        label='Descripción'
                        size='small'
                        value={editData.description}
                        onChange={(event) => setEditData((prev) => ({ ...prev, description: event.target.value }))}
                        disabled={updatingWeb}
                        fullWidth
                        multiline
                        minRows={2}
                        error={descriptionError}
                        helperText={descriptionError ? 'La descripción es obligatoria' : ''}
                    />
                    <TextField
                        label='Precio de venta'
                        size='small'
                        type='number'
                        value={editData.sale_price}
                        onChange={(event) => setEditData((prev) => ({ ...prev, sale_price: event.target.value }))}
                        disabled={updatingWeb}
                        error={salePriceError}
                        helperText={salePriceError ? 'Debe ingresar un precio mayor a 0' : ''}
                        fullWidth
                    />
                    <Box display='flex' alignItems='center' justifyContent='space-between' border='1px solid' borderColor='divider' borderRadius={1.5} p={1.2}>
                        <Typography variant='body2'>Disponible en web</Typography>
                        <Switch
                            checked={draftWebEnabled}
                            onChange={(event) => setDraftWebEnabled(event.target.checked)}
                            disabled={updatingWeb}
                        />
                    </Box>
                    <Box display='flex' flexDirection='column' gap={0.7}>
                        <Typography variant='caption' color='text.secondary'>Imágenes actuales</Typography>
                        <Box display='flex' gap={0.7} flexWrap='wrap'>
                            {(editData.images || []).map((image, index) => (
                                <Box key={`old-${index}`} position='relative'>
                                    <Box component='img' src={image} alt={`img-${index}`} sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                                    <Button size='small' onClick={() => handleRemoveExistingImage(index)} disabled={updatingWeb} sx={{ minWidth: 'auto', px: 0.5, position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', lineHeight: 1 }}>×</Button>
                                </Box>
                            ))}
                        </Box>
                        {imagesError && (
                            <Typography variant='caption' color='error'>Debe haber al menos 1 imagen</Typography>
                        )}
                    </Box>
                    <Box>
                        <Button variant='outlined' disabled={updatingWeb} onClick={() => webImagesInputRef.current?.click()}>
                            Agregar imágenes
                        </Button>
                        <TextField
                            size='small'
                            placeholder='Prompt IA (opcional)'
                            value={imagePrompt}
                            onChange={(e) => setImagePrompt(e.target.value)}
                            sx={{ mt: 0.75, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 } }}
                        />
                        <input
                            ref={webImagesInputRef}
                            hidden
                            type='file'
                            accept='image/*'
                            multiple
                            onChange={handleSelectImages}
                        />
                        {newImageFiles.length > 0 && (
                            <Box mt={1} display='flex' gap={0.7} flexWrap='wrap'>
                                {newImageFiles.map((file, index) => (
                                    <Chip key={`new-${file.name}-${index}`} label={file.name} onDelete={() => handleRemoveNewImage(index)} size='small' />
                                ))}
                            </Box>
                        )}
                    </Box>

                    <Box display='flex' flexDirection='column' gap={1} mt={0.5}>
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography variant='subtitle2'>Variantes</Typography>
                            <Button startIcon={<AddIcon />} size='small' variant='outlined' onClick={handleAddVariant} disabled={updatingWeb}>
                                Agregar color
                            </Button>
                        </Box>
                        {typeDetails.length === 0 && (
                            <Typography variant='caption' color='text.secondary'>No hay variantes cargadas. Agregá al menos una si vendés por talle/color.</Typography>
                        )}
                        {typeDetails.map((variant, index) => (
                            <VariantCard
                                key={variant._id || `${index}-${variant.color || 'variant'}`}
                                variant={variant}
                                index={index}
                                onRemove={handleRemoveVariant}
                                onChange={handleVariantChange}
                                onChangeSizeDetails={handleSizeDetailsChange}
                                onAddSize={handleAddSize}
                                onRemoveSize={handleRemoveSize}
                                onAddImage={handleAddVariantImage}
                                onRemoveImage={handleRemoveVariantImage}
                            />
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setWebModalOpen(false)} disabled={updatingWeb}>Cancelar</Button>
                <Button variant='contained' onClick={handleConfirmEnableWeb} disabled={updatingWeb || !draftWebEnabled || !isValidForm}>Guardar</Button>
            </DialogActions>
        </Dialog>
    </ListItem>
}

export default ArticleData;